import { ERequestType } from '../../ERequestType';

export interface ClassificationGlossary {
    description: string;
}

export const CLASSIFICATION_GLOSSARY: Record<ERequestType, Record<string, ClassificationGlossary>> = {
    [ERequestType.ISSUE]: {
        'Change Order': {
            description:
                'Use when modifications or cancellations to an existing order or PO are requested (e.g., change quantities, ship-to address, MAD/ship dates, deliveries, shipping conditions, payment type, or add/remove materials etc.)(including RUO orders).'
        },
        'Broken Product': {
            description:
                'Use when a product arrives physically damaged due to shipping or handling (includes sending return label).'
        },
        'Short Dated Expiration Date': {
            description:
                'Use when the product expiration date is too close to the current date, making it unsuitable for intended shelf life (includes sending return label).'
        },
        'Missing Product': {
            description:
                'Use when one or more products listed on the PO or order were not delivered.\nFor example, the item appears on the PO, order, or packing list but is not included in the shipment (not in the box at all).'
        },
        'Wrong Quantity': {
            description:
                'Use when the quantity received does not match the quantity ordered (includes sending return label).'
        },
        'Delivery issue': {
            description:
                'Use when the entire order has not been received/delivered, delivered to the wrong address, or delivered late (includes sending return label).'
        },
        'Wrong Product': {
            description:
                'Use when an incorrect product was processed or delivered (e.g., wrong product, wrong lot or lot matching issues) (includes sending return label).'
        },
        'GHX, EDI, Webshop': {
            description:
                'Use when there are troubleshooting questions or issues related to GHX, EDI, MDLA, Webshop, or on-line education'
        },
        'Product Temp': {
            description:
                'Use when a product arrives at an incorrect temperature (e.g., refrigerated items warm, frozen items thawed) (includes sending return label)'
        },
        'System Testing': {
            description: 'Use when performing UAT/smoke testing of systems.'
        },
        'Technical Replacement': {
            description:
                'Use when a product requires replacement due quality issues, technical defect, Siemens Healthineers Technical Applications Specialist/Customer Service Engineer calls in for product replacement, or malfunction unrelated to shipping damage. Notification number must be provided (includes sending return label).'
        }
    },
    [ERequestType.QUESTION_ORDER]: {
        Allocation: {
            description:
                'Use when information is needed about backorders, or stockouts status, request allocation or SET/AAT escalations.'
        },
        'Product Inquiry': {
            description:
                'Use when information is needed about, lot/batch, expiration date, product / inventory availability, product storage location moves, IFUs (instructions for use), package insert requests or additional information from a planner, return request denial.'
        },
        'POD / Packing List': {
            description: 'Use when proof of delivery documentation or packing list is requested for an order.'
        },
        'Place an Order': {
            description:
                'Use when assistance is needed to place a new order. (including standing orders, recurring orders, RUO, IRO, Sales Sample orders).'
        },
        'Order Quote': {
            description:
                'Use when a price quote for products or services is requested (often includes creating a mock order to verify pricing).'
        },
        Sequester: {
            description:
                'Use when information is needed about QAP program, sequestered inventory or protected product availability. (not including standing orders).'
        },
        'Order Status': {
            description:
                'Use when the current status of an order is requested or clarification on standing order dates (e.g., processing, shipping, delivery).'
        },
        'System Testing': {
            description: 'Use when performing UAT/smoke testing of systems.'
        },
        Tracking: {
            description: 'Use when tracking information for a shipment is requested.'
        },
        'Order Confirmation': {
            description: 'Use when a customer calls for a copy of an order confirmation.'
        },
        'Saturday Delivery': {
            description:
                'Use when a customer requests Saturday delivery of an order. This is only applicable to orders that are eligible for Saturday delivery.'
        }
    },
    [ERequestType.QUESTION_ACCOUNT]: {
        'Credit Request': {
            description: 'Use when a customer calls to inquire about a credit request.'
        },
        'Account Set Up': {
            description:
                'Use when assistance is needed to set up an customer account, resolve account creation issues, edit account/ address information, company info / sales lead, or registration of EDI, GHX or Webshop(including communication of new account updates and Lablink).'
        },
        'System Testing': {
            description: 'Use when performing UAT/smoke testing of systems.'
        },
        Reporting: {
            description:
                'Use to request assistance with open orders, reviewing order history, and resolving billing report issues. This can also include any SAP data clean up.'
        },
        'Tax Document': {
            description: 'Use when tax exemption or W9 documentation is submitted or requested (including Tariffs).'
        },
        'Instrument Return': {
            description: 'Used by Instrument Logistics for their returns authorization process.'
        }
    },
    [ERequestType.INVOICE_INQUIRY]: {
        'Invoice Inquiry': {
            description:
                'Use for invoice-related inquiries (e.g., copy requests, clarification, resending invoice, invoice not generated).'
        },
        'System Testing': {
            description: 'Use when performing UAT/smoke testing of systems.'
        },
        'Dispute Invoice': {
            description: 'Use when disputing charges, price discrepancies, discounts, or details on an invoice.'
        }
    },
    [ERequestType.MATERIAL_AND_PRICING]: {
        'New material set-up': {
            description: 'Use when setting up a new material.'
        },
        'Change or troubleshooting to an existing material': {
            description:
                "Use when making an update to an existing material, or answering question(s) about a material that's already set up."
        },
        'Report request': {
            description: 'Use if someone has requested a report.'
        },
        'Tiered/GPO pricing': {
            description: 'Use when adding new Tiered/GPO pricing OR when making an update to it.'
        },
        'Customer-specific pricing': {
            description: 'Use when adding new customer-specific pricing OR when making an update to it.'
        },
        'Changes or troubleshooting to an existing contract': {
            description: 'Use when updating an existing contract.'
        },
        Troubleshooting: {
            description: 'Use when troubleshooting a pricing or material related issue that did not result in a change.'
        },
        Service: {
            description: 'Use when making changes or adding new Service pricing.'
        },
        'International pricing and global royalties': {
            description: 'Use when MDLA is in reference to international pricing or global royalties.'
        },
        'Pricing change to existing material': {
            description: 'Use when changing list price on an existing material.'
        },
        'Request for pricing': {
            description:
                'Use when responding to a request for pricing for individual material(s).  If you are pulling a full pricing report, use the Report request option.'
        },
        'Redirect/Duplicate': {
            description: 'For use when a request is a duplicate or was incorrectly assigned and needs to be redirected.'
        }
    }
};

export function getClassificationGlossary(
    requestType: ERequestType,
    classification: string
): ClassificationGlossary | undefined {
    return CLASSIFICATION_GLOSSARY[requestType]?.[classification];
}
